import React from 'react'
import { useGetProviderResourceListQuery } from 'redux/services/providerResources'
import { Link } from 'react-router-dom'
import { classNames } from 'shared/Functions'
import * as Sentry from '@sentry/react'
import { DocumentTextIcon } from '@heroicons/react/24/outline'

export const ProviderResources: React.FC = () => {
  const [displayProviderResourceType, setDisplayProviderResourceType] =
    React.useState<string>('')

  const {
    data: providerResources,
    isFetching,
    isLoading,
    isError,
    error
  } = useGetProviderResourceListQuery({ offset: 0, limit: 100 })

  return (
    <main className="min-h-screen bg-[#EDE6DE] px-4 py-12 md:px-8 lg:px-16">
      <div className="max-w-[1400px] mx-auto">
        <h1 className="text-4xl md:text-5xl font-serif text-[#3C2416] mb-12">
          Provider Resources
        </h1>

        <section className="flex flex-col xl:flex-row gap-8">
          <div className="xl:w-1/2">
            <div className="prose prose-lg max-w-none">
              <h2 className="text-xl text-[#A6410A] font-medium mb-4">
                Overview
              </h2>
              <p className="text-[#3C2416]">
                Here you&apos;ll find links and information about resources you
                can use as a sickle cell care provider. Please{' '}
                <Link
                  to="/contact_info"
                  className="text-[#A6410A] hover:text-[#894d35] underline"
                >
                  contact us
                </Link>{' '}
                if you&apos;d like to get another resource added, or if you have
                a resource you&apos;d like to recommend.
              </p>
            </div>
          </div>

          <div className="xl:w-1/2">
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="flex justify-between items-center p-6">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-8 h-8 text-[#A6410A]">
                    <DocumentTextIcon className="w-6 h-6" />
                  </div>
                  <h2 className="text-xl text-[#A6410A] font-medium">
                    Additional Resources
                  </h2>
                </div>
              </div>

              <div className="px-6 pb-6">
                <p className="text-[#3C2416]">
                  You can find more information about Sickle Cell Disease and
                  links to other resources from the CDC at{' '}
                  <a
                    href="https://www.cdc.gov/sickle-cell/hcp/information/index.html"
                    className="text-[#A6410A] hover:text-[#894d35] underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    this link
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-white rounded-xl shadow-md p-8">
            <h2 className="text-2xl font-semibold text-[#142841] mb-4">
              Sickle Cell Advanced Practice Provider Opportunities, Resources,
              and Training (SAPPORT)
            </h2>

            <div className="prose prose-lg max-w-none">
              <p className="text-[#3C2416] mb-4">
                The SAPPORT program is a self-paced, one-year curriculum for
                advanced practice providers (APPs) who are interested in
                pursuing additional training in the clinical management of
                sickle cell disease (SCD). Established in 2021, the SAPPORT
                program seeks to increase the number of SCD providers
                nationally, connect new SCD providers with SCD experts at
                regional comprehensive sickle cell centers, and build peer
                networks for ongoing support. Successful completion of the
                program requires participation in monthly sickle cell
                telementoring sessions, an in-person observership which can be
                completed at a local institution or at a regional comprehensive
                sickle cell center, and self-study with a comprehensive online
                lecture curriculum. Graduates of the SAPPORT program will
                receive a certificate of completion from the National Alliance
                of Sickle Cell Centers.
              </p>

              <p className="text-[#3C2416] mb-6">
                For more information regarding the program, you can download the
                resource below.
              </p>

              <div className="mb-6">
                <a
                  href="https://www.dropbox.com/scl/fi/1hx2s6p7w8nuexgelg824/SAPPORT-Program-Overview-and-FAQ.docx?rlkey=5wst0utkd23227izepevv5m1b&st=63dyut2f&dl=0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-[#142841] text-white rounded-md hover:bg-[#1a3252] transition-colors duration-200"
                >
                  Download SAPPORT Program Overview and FAQ
                  <svg
                    className="ml-2 w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </a>
              </div>

              <p className="text-[#3C2416]">
                For enrollment, please use the following link:{' '}
                <a
                  href="https://jhmi.co1.qualtrics.com/jfe/form/SV_0q60sxtJfifNCwR"
                  className="text-[#A6410A] hover:text-[#894d35] underline"
                >
                  SAPPORT enrollment
                </a>
                . If you have any questions, please contact{' '}
                <a
                  href="mailto:sinerge@jhmi.edu"
                  className="text-[#A6410A] hover:text-[#894d35] underline"
                >
                  sinerge@jhmi.edu
                </a>
                .
              </p>
            </div>
          </div>
        </section>

        <section className="mt-12">
          {(isFetching || isLoading) && (
            <h2 className="text-xl text-[#3C2416] font-medium">
              Fetching resources...
            </h2>
          )}

          {!(isFetching && isLoading) && providerResources && (
            <div>
              <div className="flex flex-wrap gap-2 mb-8">
                {/* Get unique tags */}
                {Array.from(
                  new Set(
                    providerResources.flatMap((resource) =>
                      resource.tags.map((tag) => tag.color)
                    )
                  )
                ).map((tagColor) => {
                  const tag = providerResources
                    .flatMap((r) => r.tags)
                    .find((t) => t.color === tagColor)
                  return (
                    <button
                      key={tagColor}
                      onClick={() => setDisplayProviderResourceType(tagColor)}
                      className={classNames([
                        'px-4 py-2 rounded-full text-sm font-medium transition-colors',
                        displayProviderResourceType === tagColor
                          ? 'bg-[#A6410A] text-white'
                          : 'bg-[#EDE6DE] text-[#3C2416] hover:bg-[#894d35] hover:text-white'
                      ])}
                    >
                      {tag?.name}
                    </button>
                  )
                })}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {providerResources
                  .filter((resource) =>
                    displayProviderResourceType === ''
                      ? resource
                      : resource.tags
                          .map((tag) => tag.color)
                          .includes(displayProviderResourceType)
                  )
                  .map((resource) => (
                    <div
                      key={resource.name}
                      className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-200"
                    >
                      <div className="flex flex-wrap gap-2 mb-4">
                        {resource.tags.map((tag) => (
                          <span
                            key={tag.name}
                            className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-[#A6410A] text-white"
                          >
                            {tag.name}
                          </span>
                        ))}
                      </div>

                      {resource.webLink ? (
                        <a
                          href={resource.webLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block mb-3 group"
                        >
                          <h3 className="text-xl font-semibold text-[#A6410A] group-hover:text-[#894d35] transition-colors">
                            {resource.name}
                          </h3>
                        </a>
                      ) : (
                        <h3 className="text-xl font-semibold text-[#3C2416] mb-3">
                          {resource.name}
                        </h3>
                      )}

                      <p className="text-[#3C2416] mb-4">
                        {resource.description}
                      </p>

                      <div className="text-sm text-[#142841] mb-4">
                        <strong>Authors:</strong> {resource.authors}
                      </div>

                      <div className="space-y-2">
                        {resource.instructionsDownloadLink && (
                          <a
                            href={resource.instructionsDownloadLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-[#A6410A] hover:text-[#894d35] font-medium transition-colors"
                          >
                            Download the instructions for this resource
                          </a>
                        )}
                        {resource.resourceDownloadLink && (
                          <a
                            href={resource.resourceDownloadLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-[#A6410A] hover:text-[#894d35] font-medium transition-colors"
                          >
                            Download the resource
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {!isFetching &&
            !isLoading &&
            !isError &&
            providerResources?.length === 0 && (
              <h2 className="text-xl text-[#3C2416] font-medium">
                There are no resources to display.
              </h2>
            )}

          {isError && Sentry.captureException(error) && (
            <h2 className="text-xl text-[#3C2416] font-medium">
              There was an issue fetching resources. Our apologies - we&apos;re
              working on fixing the issue as quickly as possible.
            </h2>
          )}
        </section>
      </div>
    </main>
  )
}
